import BaseComponentController from "./base_component_controller"
import { enter, leave } from "../../../../design_system/transitions"

export default class extends BaseComponentController {
  static targets = ["backdrop", "panel", "input", "result"]

  static values = {
    openClass: { type: String, default: 'open' }
  }

  connect() {
    this.isOpen = false

    if (this.element.classList.contains(this.openClassValue)) {
      this.open()
    }
  }

  toggle(event) {
    this.isOpen ? this.close(event) : this.open(event)
  }

  open(event) {
    event?.preventDefault()
    if (this.isOpen) return

    this.dispatchEvent({ action: 'before-open' })
    this.animateIn()
    this.isOpen = true
    this.focusInput()
    this.dispatchEvent({ action: 'after-open' })
  }

  close(event) {
    event?.preventDefault()
    if (!this.isOpen) return

    this.dispatchEvent({ action: 'before-close' })
    this.animateOut()
    this.isOpen = false
    this.resetInput()
    this.clearHighlight()
    this.dispatchEvent({ action: 'after-close' })
  }

  animateIn() {
    enter(this.element)
    if (this.hasBackdropTarget) enter(this.backdropTarget)
    if (this.hasPanelTarget) enter(this.panelTarget)
  }

  animateOut() {
    if (this.hasBackdropTarget) leave(this.backdropTarget)
    if (this.hasPanelTarget) leave(this.panelTarget)
    leave(this.element)
  }

  highlightNext(event) {
    if (!this.isOpen) return
    this.moveHighlight(1, event)
  }

  highlightPrev(event) {
    if (!this.isOpen) return
    this.moveHighlight(-1, event)
  }

  activateHighlighted(event) {
    if (!this.isOpen) return

    const current = this.currentHighlight()
    if (!current) return

    event?.preventDefault()
    current.click()
  }

  highlight(event) {
    const target = event.currentTarget
    if (!target || target === this.currentHighlight()) return

    this.setHighlight(target)
  }

  focusInput() {
    if (!this.hasInputTarget) return
    requestAnimationFrame(() => this.inputTarget.focus())
  }

  resetInput() {
    if (this.hasInputTarget) this.inputTarget.value = ''
  }

  visibleResults() {
    if (!this.hasResultTarget) return []
    return this.resultTargets.filter(result => result.offsetParent !== null)
  }

  currentHighlight() {
    return this.visibleResults().find(result => result.getAttribute('aria-selected') === 'true')
  }

  setHighlight(element) {
    this.clearHighlight()
    element.setAttribute('aria-selected', 'true')
    element.scrollIntoView({ block: 'nearest' })
  }

  clearHighlight() {
    if (!this.hasResultTarget) return
    this.resultTargets.forEach(result => result.removeAttribute('aria-selected'))
  }

  moveHighlight(direction, event) {
    const visible = this.visibleResults()
    if (visible.length === 0) return

    event?.preventDefault()

    const current = this.currentHighlight()
    let nextIndex = 0

    if (current) {
      const currentIndex = visible.indexOf(current)
      nextIndex = (currentIndex + direction + visible.length) % visible.length
    } else if (direction === -1) {
      nextIndex = visible.length - 1
    }

    this.setHighlight(visible[nextIndex])
  }
}
